import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ChartLineUp, Watch, Star, ArrowUpRight, ArrowDown, Minus,
  ArrowsClockwise, Fire, MapPin, Bell, BellSlash,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  fetchWatchBrands, fetchWatchNews, fetchWatchIndex, fetchWatchHype, fetchWatchSentiment,
} from "../lib/api";
import { relativeTime } from "../lib/time";
import SEO from "../components/SEO";

const RANGES = [
  { key: "1m", label: "1M" },
  { key: "3m", label: "3M" },
  { key: "6m", label: "6M" },
  { key: "1y", label: "1Y" },
  { key: "2y", label: "2Y" },
];

const WATCHLIST_KEY = "sentimeter_watch_brands_v1";
const ALERTS_KEY = "sentimeter_watch_brand_alerts_v1";
const loadWatchlist = () => {
  try {
    const raw = localStorage.getItem(WATCHLIST_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};
const saveWatchlist = (arr) => {
  try {
    localStorage.setItem(WATCHLIST_KEY, JSON.stringify(arr));
  } catch {
    // ignore quota
  }
};
const loadAlerts = () => {
  try {
    const raw = localStorage.getItem(ALERTS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};
const saveAlerts = (obj) => {
  try {
    localStorage.setItem(ALERTS_KEY, JSON.stringify(obj));
  } catch {
    // ignore
  }
};

const fmtUSD = (n) => {
  if (n == null) return "—";
  if (n >= 1000) return `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return `$${n}`;
};

const tierColor = (t) => {
  if (t === "grail") return "#FFCC00";
  if (t === "high") return "#34C759";
  if (t === "mid") return "#A1A1AA";
  return "#FFFFFF99";
};

const tagFor = (s) => {
  if (s === "positive") return { c: "#34C759", icon: ArrowUpRight, label: "POS" };
  if (s === "negative") return { c: "#FF3B30", icon: ArrowDown, label: "NEG" };
  return { c: "#A1A1AA", icon: Minus, label: "NEU" };
};

function IndexChart({ data }) {
  return (
    <ResponsiveContainer width="100%" height={260}>
      <AreaChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="watchIndexGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FFCC00" stopOpacity={0.35} />
            <stop offset="100%" stopColor="#FFCC00" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="rgba(255,255,255,0.05)" />
        <XAxis
          dataKey="date"
          tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "IBM Plex Mono" }}
          axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
          tickLine={false}
          minTickGap={40}
        />
        <YAxis
          tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "IBM Plex Mono" }}
          axisLine={false}
          tickLine={false}
          width={50}
          domain={["dataMin - 2", "dataMax + 2"]}
          tickFormatter={(v) => v.toFixed(0)}
        />
        <Tooltip
          contentStyle={{ background: "#000", border: "1px solid rgba(255,255,255,0.15)", fontFamily: "IBM Plex Mono", fontSize: 11 }}
          labelStyle={{ color: "#A1A1AA" }}
          formatter={(v) => [v.toFixed(2), "index"]}
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke="#FFCC00"
          strokeWidth={2}
          fill="url(#watchIndexGrad)"
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function BrandCard({ b, hype, sentiment, isWatched, onToggleWatch, alertOn, onToggleAlert }) {
  return (
    <div
      className="bg-surface border border-white/10 p-4 flex flex-col gap-2 hover:border-white/30 transition-colors duration-150"
      data-testid={`brand-card-${b.brand}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="heading text-base font-semibold tracking-tight truncate">{b.brand}</h4>
            <span
              className="mono text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 border"
              style={{ color: tierColor(b.tier), borderColor: `${tierColor(b.tier)}55` }}
            >
              {b.tier}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1 mono text-[10px] text-white/55">
            <MapPin size={10} weight="bold" />
            <span>{b.country}</span>
            <span className="text-white/20">·</span>
            <span>{fmtUSD(b.min_price)}–{fmtUSD(b.max_price)}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={() => onToggleAlert(b.brand)}
            className={`transition-colors duration-150 ${
              alertOn ? "text-neutral_zone hover:text-yellow-300" : "text-white/30 hover:text-white"
            }`}
            data-testid={`brand-alert-${b.brand}`}
            data-alert-on={alertOn ? "true" : "false"}
            title={alertOn ? "alert on — notify on sentiment / hype flip" : "alert off"}
          >
            {alertOn ? <Bell size={14} weight="fill" /> : <BellSlash size={14} weight="bold" />}
          </button>
          <button
            onClick={() => onToggleWatch(b.brand)}
            className={`transition-colors duration-150 ${
              isWatched ? "text-neutral_zone hover:text-yellow-300" : "text-white/30 hover:text-white"
            }`}
            data-testid={`watch-toggle-${b.brand}`}
            title={isWatched ? "remove from watchlist" : "add to watchlist"}
          >
            <Star size={16} weight={isWatched ? "fill" : "bold"} />
          </button>
        </div>
      </div>

      {/* Iconic models */}
      <div className="flex flex-wrap gap-1 mt-1">
        {(b.iconic || []).slice(0, 3).map((m) => (
          <span key={m} className="mono text-[10px] text-white/70 border border-white/10 px-1.5 py-0.5">
            {m}
          </span>
        ))}
      </div>

      {/* Sentiment + Hype */}
      <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-white/5">
        <div>
          <div className="overline">sentiment</div>
          <div
            className="mono text-sm font-bold"
            style={{ color: sentiment ? (sentiment.score > 55 ? "#34C759" : sentiment.score < 45 ? "#FF3B30" : "#A1A1AA") : "#666" }}
            data-testid={`brand-sentiment-${b.brand}`}
          >
            {sentiment ? sentiment.score.toFixed(0) : "—"}
            {sentiment && (
              <span className="mono text-[10px] text-white/40 ml-1.5">
                {sentiment.positive}↑{sentiment.negative}↓
              </span>
            )}
          </div>
        </div>
        <div>
          <div className="overline">hype</div>
          <div className="flex items-center gap-1">
            <Fire size={12} weight="fill" color={hype ? "#FF9500" : "#444"} />
            <span
              className="mono text-sm font-bold"
              style={{ color: hype ? "#FF9500" : "#666" }}
              data-testid={`brand-hype-${b.brand}`}
            >
              {hype ? hype.score.toFixed(0) : "—"}
            </span>
            {hype && <span className="mono text-[10px] text-white/40">×{hype.mentions}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WatchesDashboard() {
  const navigate = useNavigate();
  const [country, setCountry] = useState("All");
  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(600000);
  const [range, setRange] = useState("1y");
  const [brandsResp, setBrandsResp] = useState(null);
  const [news, setNews] = useState(null);
  const [indexData, setIndexData] = useState(null);
  const [hype, setHype] = useState(null);
  const [brandSent, setBrandSent] = useState(null);
  const [loading, setLoading] = useState({ brands: true, news: true, index: true, hype: true, sent: true });
  const [watchlist, setWatchlist] = useState(() => loadWatchlist());
  const [showWatchedOnly, setShowWatchedOnly] = useState(false);
  const [alerts, setAlerts] = useState(() => loadAlerts());
  const prevBrandState = useRef({}); // brand -> { sentScore, hypeRank }

  const toggleWatch = useCallback((brand) => {
    setWatchlist((prev) => {
      const has = prev.includes(brand);
      const next = has ? prev.filter((b) => b !== brand) : [...prev, brand];
      saveWatchlist(next);
      toast(has ? `Removed ${brand}` : `Added ${brand} to watchlist`);
      return next;
    });
  }, []);

  const toggleAlert = useCallback((brand) => {
    setAlerts((prev) => {
      const next = { ...prev, [brand]: !prev[brand] };
      saveAlerts(next);
      toast(next[brand] ? `Alerts ON for ${brand}` : `Alerts off for ${brand}`, {
        description: next[brand] ? "We'll ping you on sentiment or hype flips." : undefined,
      });
      return next;
    });
  }, []);

  // Initial loads (only those that don't depend on filters)
  useEffect(() => {
    setLoading((l) => ({ ...l, news: true, hype: true, sent: true }));
    fetchWatchNews(20)
      .then((d) => setNews(d))
      .catch(() => setNews(null))
      .finally(() => setLoading((l) => ({ ...l, news: false })));
    fetchWatchHype()
      .then((d) => setHype(d))
      .catch(() => setHype(null))
      .finally(() => setLoading((l) => ({ ...l, hype: false })));
    fetchWatchSentiment()
      .then((d) => setBrandSent(d))
      .catch(() => setBrandSent(null))
      .finally(() => setLoading((l) => ({ ...l, sent: false })));
  }, []);

  // Brands list refetches when filters change
  useEffect(() => {
    setLoading((l) => ({ ...l, brands: true }));
    fetchWatchBrands({
      country: country === "All" ? undefined : country,
      min_price: priceMin || undefined,
      max_price: priceMax || undefined,
    })
      .then((d) => setBrandsResp(d))
      .catch(() => setBrandsResp(null))
      .finally(() => setLoading((l) => ({ ...l, brands: false })));
  }, [country, priceMin, priceMax]);

  // Index reloads on range change
  useEffect(() => {
    setLoading((l) => ({ ...l, index: true }));
    fetchWatchIndex(range)
      .then((d) => setIndexData(d))
      .catch(() => setIndexData(null))
      .finally(() => setLoading((l) => ({ ...l, index: false })));
  }, [range]);

  const countries = useMemo(() => ["All", ...(brandsResp?.countries || ["Switzerland", "Germany", "Japan", "UK", "USA"])], [brandsResp]);

  const hypeByBrand = useMemo(() => {
    const map = {};
    (hype?.hype || []).forEach((h) => { map[h.brand] = h; });
    return map;
  }, [hype]);
  const sentByBrand = useMemo(() => {
    const map = {};
    (brandSent?.brands || []).forEach((b) => { map[b.brand] = b; });
    return map;
  }, [brandSent]);

  const visibleBrands = useMemo(() => {
    const all = brandsResp?.brands || [];
    return showWatchedOnly ? all.filter((b) => watchlist.includes(b.brand)) : all;
  }, [brandsResp, showWatchedOnly, watchlist]);

  // Detect per-brand sentiment / hype-rank flips for armed brands and toast.
  // Sentiment flips: bull→bear (>=55 → <=45) or bear→bull (<=45 → >=55).
  // Hype rising: brand jumps from rank > 10 (or absent) to top 5.
  useEffect(() => {
    const hypeList = hype?.hype || [];
    const hypeRank = {};
    hypeList.forEach((h, i) => { hypeRank[h.brand] = i + 1; });

    Object.keys(alerts).forEach((brand) => {
      if (!alerts[brand]) return;
      const sent = sentByBrand[brand];
      const score = sent?.score;
      const prev = prevBrandState.current[brand] || {};
      const rank = hypeRank[brand]; // undefined if not mentioned

      if (score != null && prev.sentScore != null) {
        const wasBull = prev.sentScore >= 55;
        const wasBear = prev.sentScore <= 45;
        const nowBull = score >= 55;
        const nowBear = score <= 45;
        if (wasBull && nowBear) {
          toast(`${brand}: sentiment flipped BEARISH (${prev.sentScore.toFixed(0)} → ${score.toFixed(0)}) ↓`);
        } else if (wasBear && nowBull) {
          toast(`${brand}: sentiment flipped BULLISH (${prev.sentScore.toFixed(0)} → ${score.toFixed(0)}) ↑`);
        }
      }

      const wasCold = prev.hypeRank == null || prev.hypeRank > 10;
      const nowHot = rank != null && rank <= 5;
      if (wasCold && nowHot) {
        toast(`${brand}: hype rising — now #${rank} on the leaderboard 🔥`);
      }

      prevBrandState.current[brand] = {
        sentScore: score ?? prev.sentScore,
        hypeRank: rank ?? prev.hypeRank,
      };
    });
  }, [sentByBrand, hype, alerts]);

  const armedCount = Object.values(alerts).filter(Boolean).length;

  const overall = brandSent?.overall;
  const overallScore = overall?.score;
  const overallColor = overallScore == null ? "#A1A1AA" : overallScore > 60 ? "#34C759" : overallScore < 40 ? "#FF3B30" : "#FFCC00";

  return (
    <div className="min-h-screen bg-obsidian text-white" data-testid="watches-page">
      <SEO
        title="Luxury Watch Segment Index, Brands & Hype Meter"
        path="/watches"
        description="Live luxury watch segment index (Swatch + Richemont + LVMH), AI-curated news from Hodinkee, WatchTime & A Blog to Watch, brand sentiment + hype meter across Rolex, Patek Philippe, AP, Omega, Grand Seiko and more. Filter by country of movement manufacture and price tier."
        keywords={["luxury watch index", "rolex sentiment", "patek philippe news", "watch hype meter", "swatch group stock", "richemont stock", "watch press feed", "grand seiko index"]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Luxury Watches Sentiment",
          url: "https://sent-i-meter.com/watches",
          about: { "@type": "Thing", name: "Luxury watch industry sentiment and market index" },
        }}
      />
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-white/10 px-4 md:px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/70 hover:text-white" data-testid="watches-back">
            <ArrowLeft size={16} weight="bold" />
            <span className="overline">back</span>
          </button>
          <div className="w-px h-6 bg-white/10 mx-2" />
          <button onClick={() => navigate("/")} className="w-8 h-8 bg-white text-black flex items-center justify-center">
            <ChartLineUp size={16} weight="bold" />
          </button>
          <div className="hidden sm:block">
            <div className="heading text-sm font-bold tracking-tight leading-none">LUXURY WATCHES</div>
            <div className="overline mt-0.5 leading-none">segment index · brands · hype</div>
          </div>
        </div>
        <Watch size={20} weight="duotone" color="#FFCC00" />
      </header>

      <main className="px-4 md:px-6 py-6 max-w-[1400px] mx-auto">
        {/* Hero: segment index trendline + overall sentiment */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
          <div className="bg-surface border border-white/10 p-5 lg:col-span-2" data-testid="watch-index-card">
            <div className="flex items-baseline justify-between mb-2">
              <div>
                <div className="overline">luxury watch segment · base 100</div>
                <div className="flex items-baseline gap-3 mt-1">
                  <h2 className="heading text-2xl font-bold" data-testid="watch-index-value">
                    {indexData?.value != null ? indexData.value.toFixed(2) : "—"}
                  </h2>
                  {indexData?.change_pct != null && (
                    <span
                      className="mono text-sm font-bold"
                      style={{ color: indexData.change_pct >= 0 ? "#34C759" : "#FF3B30" }}
                      data-testid="watch-index-change"
                    >
                      {indexData.change_pct >= 0 ? "+" : ""}
                      {indexData.change_pct.toFixed(2)}%
                    </span>
                  )}
                </div>
                <div className="mono text-[10px] text-white/40 mt-0.5">
                  {(indexData?.constituents || []).map((c) => c.symbol).join(" · ") || "—"}
                </div>
              </div>
              <div className="flex gap-1" data-testid="watch-range-chips">
                {RANGES.map((r) => (
                  <button
                    key={r.key}
                    onClick={() => setRange(r.key)}
                    className={`px-2 py-1 mono text-[10px] uppercase tracking-[0.14em] border transition-colors duration-150 ${
                      range === r.key
                        ? "bg-white text-black border-white"
                        : "bg-transparent text-white/60 border-white/10 hover:border-white/40 hover:text-white"
                    }`}
                    data-testid={`watch-range-${r.key}`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            {loading.index || !indexData?.series?.length ? (
              <div className="h-[260px] flex items-center justify-center overline">
                {loading.index ? "loading…" : "no data"}
              </div>
            ) : (
              <IndexChart data={indexData.series} />
            )}
          </div>

          {/* Sentiment & Hype overview */}
          <div className="bg-surface border border-white/10 p-5 flex flex-col gap-4" data-testid="watch-overall-card">
            <div>
              <div className="overline">industry sentiment · news-based</div>
              <div className="flex items-baseline gap-3 mt-1">
                <span
                  className="heading text-3xl font-bold"
                  style={{ color: overallColor }}
                  data-testid="watch-overall-score"
                >
                  {overallScore != null ? overallScore.toFixed(0) : "—"}
                </span>
                <span className="overline">/100</span>
              </div>
              {overall && (
                <div className="flex items-center gap-3 mt-2">
                  <span className="mono text-xs text-bullish">{overall.positive}↑</span>
                  <span className="mono text-xs text-white/60">{overall.neutral}=</span>
                  <span className="mono text-xs text-bearish">{overall.negative}↓</span>
                  <span className="overline ml-auto">{brandSent?.based_on_items || 0} headlines</span>
                </div>
              )}
            </div>

            <div className="border-t border-white/5 pt-3">
              <div className="flex items-center gap-1.5 mb-2">
                <Fire size={12} weight="fill" color="#FF9500" />
                <span className="overline">hype meter · top 5</span>
              </div>
              <div className="space-y-1.5" data-testid="hype-leaderboard">
                {(hype?.hype || []).slice(0, 5).map((h, i) => (
                  <div key={h.brand} className="flex items-center gap-2 text-xs" data-testid={`hype-row-${i}`}>
                    <span className="mono text-white/40 w-4">{i + 1}</span>
                    <span className="flex-1 truncate">{h.brand}</span>
                    <span className="mono text-white/55 text-[10px]">{h.country?.slice(0, 3).toUpperCase()}</span>
                    <div className="h-1.5 w-16 bg-white/5">
                      <div className="h-full bg-orange-400" style={{ width: `${Math.min(100, h.score)}%`, backgroundColor: "#FF9500" }} />
                    </div>
                    <span className="mono text-[10px] text-white/60 w-8 text-right">×{h.mentions}</span>
                  </div>
                ))}
                {(!hype?.hype || hype.hype.length === 0) && <div className="overline">no recent mentions</div>}
              </div>
            </div>
          </div>
        </section>

        {/* Filters + Brand catalog */}
        <section className="bg-surface border border-white/10 p-5 mb-6" data-testid="watch-filters">
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div>
              <div className="overline mb-1.5">country of movement</div>
              <div className="flex flex-wrap gap-1">
                {countries.map((c) => (
                  <button
                    key={c}
                    onClick={() => setCountry(c)}
                    className={`px-2 py-1 mono text-[10px] uppercase tracking-[0.14em] border transition-colors duration-150 ${
                      country === c
                        ? "bg-white text-black border-white"
                        : "bg-transparent text-white/60 border-white/10 hover:border-white/40 hover:text-white"
                    }`}
                    data-testid={`watch-country-${c}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center justify-between mb-1">
                <span className="overline">price range · usd</span>
                <span className="mono text-xs text-white/70" data-testid="watch-price-display">
                  {fmtUSD(priceMin)} – {fmtUSD(priceMax)}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="range" min="0" max="100000" step="500" value={priceMin}
                  onChange={(e) => setPriceMin(Math.min(+e.target.value, priceMax))}
                  className="flex-1 accent-bullish"
                  data-testid="watch-price-min"
                />
                <input
                  type="range" min="0" max="600000" step="1000" value={priceMax}
                  onChange={(e) => setPriceMax(Math.max(+e.target.value, priceMin))}
                  className="flex-1 accent-bullish"
                  data-testid="watch-price-max"
                />
              </div>
            </div>

            <div>
              <div className="overline mb-1.5">view</div>
              <button
                onClick={() => setShowWatchedOnly((v) => !v)}
                className={`px-3 py-1.5 mono text-[10px] uppercase tracking-[0.16em] border flex items-center gap-1.5 transition-colors duration-150 ${
                  showWatchedOnly
                    ? "bg-neutral_zone text-black border-neutral_zone"
                    : "bg-transparent text-white/60 border-white/10 hover:border-white/40 hover:text-white"
                }`}
                data-testid="watch-show-watched"
              >
                <Star size={12} weight={showWatchedOnly ? "fill" : "bold"} />
                {showWatchedOnly ? "Watching" : "All Brands"} ({watchlist.length})
              </button>
            </div>
          </div>

          {/* Grid */}
          {loading.brands ? (
            <div className="overline py-10 text-center">loading brands…</div>
          ) : visibleBrands.length === 0 ? (
            <div className="overline py-10 text-center" data-testid="watch-empty">
              no brands match the current filters
            </div>
          ) : (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
              data-testid="watch-brand-grid"
            >
              {visibleBrands.map((b) => (
                <BrandCard
                  key={b.brand}
                  b={b}
                  hype={hypeByBrand[b.brand]}
                  sentiment={sentByBrand[b.brand]}
                  isWatched={watchlist.includes(b.brand)}
                  onToggleWatch={toggleWatch}
                  alertOn={!!alerts[b.brand]}
                  onToggleAlert={toggleAlert}
                />
              ))}
            </div>
          )}
          <div className="overline mt-3" data-testid="watch-brand-count">
            {visibleBrands.length} brands shown · {watchlist.length} watching
            {armedCount > 0 && (
              <span className="ml-3 inline-flex items-center gap-1 text-neutral_zone" data-testid="watch-alerts-armed">
                <Bell size={10} weight="fill" />
                {armedCount} alert{armedCount === 1 ? "" : "s"} armed
              </span>
            )}
          </div>
        </section>

        {/* Watch news feed */}
        <section className="bg-surface border border-white/10" data-testid="watch-news-card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <h3 className="heading text-lg font-semibold">Latest from Watch Press</h3>
            <span className="overline">
              hodinkee · watchtime · monochrome · fratello · revolution · watchpro · ablogtowatch
            </span>
          </div>
          <div className="overflow-y-auto max-h-[520px]">
            {loading.news && (
              <div className="p-5 space-y-2">
                <div className="h-3 bg-white/5 w-full" />
                <div className="h-3 bg-white/5 w-10/12" />
                <div className="h-3 bg-white/5 w-9/12" />
              </div>
            )}
            {!loading.news && (!news?.items || news.items.length === 0) && (
              <div className="overline p-6 text-center">no headlines</div>
            )}
            {(news?.items || []).map((h, i) => {
              const t = tagFor(h.sentiment);
              const Icon = t.icon;
              return (
                <a
                  key={i}
                  href={h.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-5 py-3 border-b border-white/5 hover:bg-white/[0.03] transition-colors duration-150"
                  data-testid={`watch-news-${i}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex flex-col items-center mt-0.5" style={{ color: t.c, minWidth: 28 }}>
                      <Icon size={14} weight="bold" />
                      <span className="mono text-[9px] mt-0.5">{t.label}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm leading-snug">{h.title}</div>
                      <div className="mono text-[10px] text-white/40 mt-1 flex items-center gap-2">
                        <span className="truncate">{h.source || "press"}</span>
                        {h.published_iso && (
                          <>
                            <span className="text-white/20">·</span>
                            <span className="shrink-0 text-white/55">{relativeTime(h.published_iso)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </section>

        <footer className="border-t border-white/10 pt-4 mt-8 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="overline hover:text-white" data-testid="watches-footer-back">
            ← back to dashboard
          </button>
          <span className="overline">illustrative · brand price ranges are approximate retail</span>
        </footer>
      </main>
    </div>
  );
}
